import React, { useRef, useState } from 'react';
import { debounce } from 'lodash';
import { Select, Option } from '@mm/components';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { CampaignAudienceAttribute } from '@mm/state/model/CampaignsModel';
import { useAudiencesReducer } from './use-audiences-reducer';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { useAudienceViewHandlers } from '../../../beta/modules/Audiences/use-audience-view-handlers';
import { MenuItem } from '../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import './AudiencesSelector.pcss';

type Props = {
  className?: string;
  disabled?: boolean;
  label?: string;
  selected?: Option[];
  onClose?: () => void;
  onChange?: (v: Option[]) => void;
  withThreeDotMenu?: boolean;
  placeholder?: string;
  onlyEsAudiences?: boolean;
  singleSelect?: boolean;
  hideCount?: boolean;
};

export function mapAudienceAttribute({
  name = '',
  audienceId,
  ...rest
}: CampaignAudienceAttribute) {
  return {
    ...rest,
    label: name,
    id: audienceId,
  } as Option;
}

export function mapAudience({
  name: label,
  id,
  contactsCount,
}: AudiencesModel) {
  return {
    label,
    id,
    contactsCount,
  } as Option;
}

export const AudiencesSelector = ({
  disabled,
  label,
  className,
  selected,
  onClose,
  onChange,
  withThreeDotMenu = false,
  placeholder,
  onlyEsAudiences,
  singleSelect,
  hideCount,
}: Props) => {
  const [audienceId, setAudienceId] = useState(0);
  const { state, dispatcher } = useAudiencesReducer();
  const { onEdit } = useAudienceViewHandlers();
  const { busy, pagination, entities } = state;

  const options = entities
    .filter(entity => (onlyEsAudiences ? entity.type === 'es_based' : true))
    .map(mapAudience);

  const onOpen = () => dispatcher.retrieve();

  const onCloseLocal = () => {
    // prevent debounced search from firing after select closed
    onClose?.();
    onSearch.cancel();
    dispatcher.reset();
  };

  const onScrollEnd =
    pagination?.isLastPage || busy ? undefined : dispatcher.retrieve;

  const searchDebounced = debounce(dispatcher.search, 400);

  const onSearch = useRef<typeof searchDebounced>(searchDebounced).current;
  const elements: MenuItem[] = [{ name: 'Edit', value: 'edit' }];

  const menuProps = {
    className: 'drip-audience-selector__three-dot-menu',
    contentClassName: 'drip-audience-selector__dropdown_content',
    elements,
    selectItem: (val?: string) => {
      switch (val) {
        case 'edit':
          if (audienceId > 0) {
            return onEdit(audienceId);
          }
          break;
        default:
          break;
      }
    },
  };

  const select = (
    <Select
      className={className}
      label={label}
      placeholder={placeholder ?? 'Select Audiences'}
      selected={selected}
      disabled={disabled}
      options={options}
      onOpen={onOpen}
      onClose={onCloseLocal}
      onChange={onChange}
      onSearch={onSearch}
      onScrollEnd={onScrollEnd}
      isLoading={busy}
      menuProps={menuProps}
      withThreeDotMenu={withThreeDotMenu}
      singleSelect={singleSelect}
      hideCount={hideCount}
      setAudienceId={setAudienceId}
      disableClickOutsideExceptions
    />
  );

  if (disabled && selected?.length) {
    const text = selected.map(({ label }) => label).join(', ');
    return (
      <PrimaryToolTip
        anchorElement={select}
        useHover
        position="bottom"
        align="center"
      >
        {text}
      </PrimaryToolTip>
    );
  }

  return select;
};
